import axios from 'axios';
import '../config.js';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
  };
};

const firstDefined = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    if (values[i] !== undefined && values[i] !== null) {
      return values[i];
    }
  }

  return null;
};

const toNumberArray = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));
};

const normalizeErrorItem = (item) => {
  if (!item || typeof item !== 'object') {
    return {
      hubId: null,
      message: 'Unknown error',
    };
  }

  return {
    hubId: firstDefined(item.hubId, item.HubId, item.id, item.Id, null),
    message: String(firstDefined(item.message, item.Message, 'Unknown error')),
  };
};

const normalizeEnforceStaffCodeResult = (data) => {
  const payload = data && typeof data === 'object' ? data : {};
  const errors = firstDefined(payload.errors, payload.Errors, []);

  return {
    targetHubIds: toNumberArray(firstDefined(payload.targetHubIds, payload.TargetHubIds, [])),
    succeededHubIds: toNumberArray(firstDefined(payload.succeededHubIds, payload.SucceededHubIds, [])),
    offlineHubIds: toNumberArray(firstDefined(payload.offlineHubIds, payload.OfflineHubIds, [])),
    failedHubIds: toNumberArray(firstDefined(payload.failedHubIds, payload.FailedHubIds, [])),
    errors: Array.isArray(errors)
      ? errors.map(normalizeErrorItem)
      : [],
  };
};

const normalizeRegenerateStaffCodeResult = (data) => {
  const payload = data && typeof data === 'object' ? data : {};
  const errors = firstDefined(payload.errors, payload.Errors, []);

  return {
    staffCode: String(firstDefined(payload.staffCode, payload.StaffCode, '')),
    rotationPolicy: String(firstDefined(payload.rotationPolicy, payload.RotationPolicy, '')),
    expiryUtc: String(firstDefined(payload.expiryUtc, payload.ExpiryUtc, '')),
    targetHubIds: toNumberArray(firstDefined(payload.targetHubIds, payload.TargetHubIds, [])),
    succeededHubIds: toNumberArray(firstDefined(payload.succeededHubIds, payload.SucceededHubIds, [])),
    offlineHubIds: toNumberArray(firstDefined(payload.offlineHubIds, payload.OfflineHubIds, [])),
    failedHubIds: toNumberArray(firstDefined(payload.failedHubIds, payload.FailedHubIds, [])),
    errors: Array.isArray(errors)
      ? errors.map(normalizeErrorItem)
      : [],
  };
};

const normalizeBulkDeleteFailedCode = (item) => {
  if (!item || typeof item !== 'object') {
    return {
      ajaxKeyPadCodeId: null,
      message: 'Unknown error',
    };
  }

  const rawId = firstDefined(
    item.ajaxKeyPadCodeId,
    item.AjaxKeyPadCodeId,
    item.id,
    item.Id,
    null
  );
  const parsedId = Number(rawId);

  return {
    ajaxKeyPadCodeId: Number.isFinite(parsedId) ? parsedId : null,
    message: String(firstDefined(item.message, item.Message, 'Unknown error')),
  };
};

const normalizeBulkDeleteKeypadCodesResult = (data) => {
  const payload = data && typeof data === 'object' ? data : {};
  const failedCodes = firstDefined(payload.failedCodes, payload.FailedCodes, []);

  return {
    deletedIds: toNumberArray(firstDefined(payload.deletedIds, payload.DeletedIds, [])),
    notFoundIds: toNumberArray(firstDefined(payload.notFoundIds, payload.NotFoundIds, [])),
    failedCodes: Array.isArray(failedCodes)
      ? failedCodes.map(normalizeBulkDeleteFailedCode)
      : [],
  };
};

/**
 * @param {number|string} locationId
 * @returns {Promise<import('./types/keypadCodes').EnforceStaffCodeResult>}
 */
export const enforceStaffCode = async (locationId) => {
  const response = await axios.post(
    `${global.config.url}/locations/keypadcode/${locationId}/enforce-staff`,
    null,
    {
      headers: {
        ...authHeaders(),
      },
    }
  );

  return normalizeEnforceStaffCodeResult(response.data);
};

/**
 * @param {number|string} locationId
 * @returns {Promise<import('./types/keypadCodes').RegenerateStaffCodeResult>}
 */
export const regenerateStaffCode = async (locationId) => {
  const response = await axios.post(
    `${global.config.url}/locations/keypadcode/${locationId}/regenerate-staff`,
    null,
    {
      headers: {
        ...authHeaders(),
      },
    }
  );

  return normalizeRegenerateStaffCodeResult(response.data);
};

/**
 * @param {number|string} locationId
 * @param {Array<number|string>} ajaxKeyPadCodeIds
 * @returns {Promise<import('./types/keypadCodes').BulkDeleteKeypadCodesResult>}
 */
export const bulkDeleteKeypadCodes = async (locationId, ajaxKeyPadCodeIds) => {
  const response = await axios.post(
    `${global.config.url}/locations/keypadcode/${locationId}/delete-bulk`,
    {
      ajaxKeyPadCodeIds: toNumberArray(ajaxKeyPadCodeIds),
    },
    {
      headers: {
        ...authHeaders(),
      },
    }
  );

  return normalizeBulkDeleteKeypadCodesResult(response.data);
};

const keypadCodesApi = {
  bulkDeleteKeypadCodes,
  enforceStaffCode,
  regenerateStaffCode,
};

export default keypadCodesApi;